import type { WeatherOption } from '../types';

/**
 * 날씨 전환.
 *
 * 카탈로그의 WeatherOption을 변형하지 않습니다(프리셋은 불변이어야 다시 고를 때 같은
 * 값이 나옵니다). 대신 두 프리셋 사이를 보간한 **런타임 날씨**를 새로 만들어 물리·렌더가
 * 같은 값을 읽게 합니다.
 *
 * 강수는 categorical이라 그대로 섞을 수 없습니다. rain/snow 세기를 따로 두고 각각
 * 0으로 줄였다가 올려서 비에서 눈으로 바로 튀지 않게 합니다.
 */

export interface RuntimeWeather {
  id: string;
  name: string;
  description: string;
  icon: string;
  rainIntensity: number;
  snowIntensity: number;
  fogColor: string;
  fogNear: number;
  fogFar: number;
  ambientLightMultiplier: number;
  directLightMultiplier: number;
  environmentIntensityMultiplier: number;
  backgroundIntensityMultiplier: number;
  roadWetness: number;
  roadTint: string;
  gripMultiplier: number;
  rollingResistanceMultiplier: number;
  /** 눈 노면 색을 쓰는지. 지면·패치 재질이 참고합니다. */
  snowy: boolean;
}

/** 안개가 없는 프리셋은 "아주 먼 안개"로 취급해 보간이 끊기지 않게 합니다. */
const CLEAR_FOG = { color: '#cfd8de', near: 600, far: 3200 };

function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

function parseHex(hex: string): [number, number, number] {
  const value = hex.replace('#', '');
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}

function toHex(channels: [number, number, number]): string {
  return `#${channels
    .map((channel) => Math.round(Math.min(255, Math.max(0, channel))).toString(16).padStart(2, '0'))
    .join('')}`;
}

export function mixColor(from: string, to: string, t: number): string {
  const a = parseHex(from);
  const b = parseHex(to);
  return toHex([lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)]);
}

/**
 * 프리셋을 런타임 날씨로 변환합니다.
 *
 * `into`를 받는 이유는 프레임 루프에서 매번 객체를 만들지 않기 위해서입니다
 * (sampleDrivingTerrain과 같은 규약입니다).
 */
export function toRuntimeWeather(
  weather: WeatherOption,
  into?: RuntimeWeather
): RuntimeWeather {
  const fog = weather.fog ?? CLEAR_FOG;
  const result = into ?? ({} as RuntimeWeather);
  return Object.assign(result, {
    id: weather.id,
    name: weather.name,
    description: weather.description,
    icon: weather.icon,
    rainIntensity: weather.precipitation === 'rain' ? weather.precipitationIntensity : 0,
    snowIntensity: weather.precipitation === 'snow' ? weather.precipitationIntensity : 0,
    fogColor: fog.color,
    fogNear: fog.near,
    fogFar: fog.far,
    ambientLightMultiplier: weather.ambientLightMultiplier,
    directLightMultiplier: weather.directLightMultiplier,
    environmentIntensityMultiplier: weather.environmentIntensityMultiplier,
    backgroundIntensityMultiplier: weather.backgroundIntensityMultiplier,
    roadWetness: weather.roadWetness,
    roadTint: weather.roadTint,
    gripMultiplier: weather.gripMultiplier,
    rollingResistanceMultiplier: weather.rollingResistanceMultiplier,
    snowy: weather.precipitation === 'snow',
  });
}

/** 두 런타임 날씨를 t(0~1)로 섞어 `into`에 씁니다. 식별자를 유지해 재렌더를 막습니다. */
export function mixRuntimeWeatherInto(
  from: RuntimeWeather,
  to: RuntimeWeather,
  t: number,
  into: RuntimeWeather
): RuntimeWeather {
  const clamped = Math.min(1, Math.max(0, t));
  return Object.assign(into, {
    id: clamped >= 1 ? to.id : `${from.id}->${to.id}`,
    name: clamped >= 0.5 ? to.name : from.name,
    description: clamped >= 0.5 ? to.description : from.description,
    icon: clamped >= 0.5 ? to.icon : from.icon,
    rainIntensity: lerp(from.rainIntensity, to.rainIntensity, clamped),
    snowIntensity: lerp(from.snowIntensity, to.snowIntensity, clamped),
    fogColor: mixColor(from.fogColor, to.fogColor, clamped),
    fogNear: lerp(from.fogNear, to.fogNear, clamped),
    fogFar: lerp(from.fogFar, to.fogFar, clamped),
    ambientLightMultiplier: lerp(from.ambientLightMultiplier, to.ambientLightMultiplier, clamped),
    directLightMultiplier: lerp(from.directLightMultiplier, to.directLightMultiplier, clamped),
    environmentIntensityMultiplier: lerp(
      from.environmentIntensityMultiplier,
      to.environmentIntensityMultiplier,
      clamped
    ),
    backgroundIntensityMultiplier: lerp(
      from.backgroundIntensityMultiplier,
      to.backgroundIntensityMultiplier,
      clamped
    ),
    roadWetness: lerp(from.roadWetness, to.roadWetness, clamped),
    roadTint: mixColor(from.roadTint, to.roadTint, clamped),
    gripMultiplier: lerp(from.gripMultiplier, to.gripMultiplier, clamped),
    rollingResistanceMultiplier: lerp(
      from.rollingResistanceMultiplier,
      to.rollingResistanceMultiplier,
      clamped
    ),
    snowy: clamped >= 0.5 ? to.snowy : from.snowy,
  });
}

/** 새 객체가 필요한 호출자(테스트·일회성 계산)를 위한 편의 래퍼입니다. */
export function mixRuntimeWeather(
  from: RuntimeWeather,
  to: RuntimeWeather,
  t: number
): RuntimeWeather {
  return mixRuntimeWeatherInto(from, to, t, { ...from });
}

/** 동적 날씨가 순환하는 순서입니다. */
export const DYNAMIC_WEATHER_SEQUENCE = ['clear', 'overcast', 'rain', 'overcast'] as const;
/** 한 단계에 머무는 시간과 전환 시간(초). */
export const DYNAMIC_WEATHER_HOLD_SECONDS = 26;
export const DYNAMIC_WEATHER_BLEND_SECONDS = 9;
